import { useState } from "react";
import { useHandlePreSignFetch, useHandlePreSignDownload, useHandlePreSignDelete, useHandlePreSignUpload, useHandlePreSignEntry, useHandlePreSignUploadAWS } from "../mutations";

type useHandlePreSignProps = {
    onRefetch?: () => void;
}

export const useHandlePreSign = ({ onRefetch }: useHandlePreSignProps = {}) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);

    const handlePreSignFetch = useHandlePreSignFetch();
    const handlePreSignDownload = useHandlePreSignDownload();
    const handlePreSignDelete = useHandlePreSignDelete();
    const handlePreSignUpload = useHandlePreSignUpload();
    const handlePreSignUploadAWS = useHandlePreSignUploadAWS();
    const handlePreSignEntry = useHandlePreSignEntry();

    const handleFetchFiles = async (
        objectId: string,
        fileType: string,
        // fileType: "DRA",
    ) => {
        let files = [];
        setLoading(true);
        setError(null);
        try {
            const response = await handlePreSignFetch.mutateAsync({ objectId, fileType });
            if (response.status) {
                files = response.data;
            } else {
                files = [];
                setError('Failed to update files !')
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
        return files;
    }

    const handleDownload = async (file: any) => {
        setLoading(true);
        setError(null);
        const params = {
            content_type: file.type,
            file_name: file.name,
            key: file.s3_path,
        };
        try {
            const response = await handlePreSignDownload.mutateAsync(params);
            const url = response?.data;
            if (url) {
                window.open(url, "_blank");
            } else {
                setError("Download URL not found");
            }
        } catch (error: any) {
            setError(error.message || "Failed to download file");
        } finally {
            setLoading(false);
        }
    };

    const handleGetThumbnail = async (file: any) => {
        setLoading(true);
        setError(null);
        const params = {
            content_type: file.content_type,
            file_name: file.file_name,
            key: file.key,
        };
        try {
            const response = await handlePreSignDownload.mutateAsync(params);
            return response?.data;
        } catch (error: any) {
            setError(error.message || "Failed to download file");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (
        fileKey: any
    ) => {
        setLoading(true);
        setError(null);
        const params = {
            "key": fileKey.s3_path,
            "file_id": fileKey.file_id
        }
        try {
            const response = await handlePreSignDelete.mutateAsync(params);
            if (response.status) {
                if (onRefetch) onRefetch();
            } else {
                setError('Failed to update files !')
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleUpload = async (
        // fileType: "DRA" | "EXCAVATION" | "SAMPLING" | "SAMPLING_LAB_RESULTS" | "SITE_ASSESMENT",
        fileType: string,
        id: string,
        file: { file: File; name: string; type: string; size: number; dataUrl: string; uploading: boolean; }
    ): Promise<any> => {
        setLoading(true);
        setError(null);
        const params = {
            "id": id.toString(),
            "file_type": fileType,
            "file_name": file.name
        }
        try {
            const response = await handlePreSignUpload.mutateAsync(params);
            if (response.status) {
                const formData = new FormData();
                Object.keys(response.data.url.fields).forEach(key => {
                    formData.append(key, response.data.url.fields[key]);
                });
                formData.append('file', file.file);
                const params = {
                    url: response.data.url.url,
                    fields: formData
                }
                return await handlePreSignUploadAWS.mutateAsync(params).then(async () => {
                    return await handleMakeEntry(id, response.data.key, response.data.attachmentId, fileType, file);
                })
            } else {
                setError('Failed to update files !')
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleMakeEntry = async (
        id: string,
        key: string,
        fileUuid: string,
        fileType: string,
        file: { file: File; name: string; type: string; size: number; dataUrl: string; uploading: boolean; }
    ): Promise<boolean> => {
        setLoading(true);
        setError(null);
        const params = {
            "id": Number(id),
            "key": key,
            "file_uuid": fileUuid,
            "file_type": fileType,
            "file_name": file.name,
            "file_size": file.size.toString(),
            "mime_type": file.type.toString(),
        }
        try {
            const response = await handlePreSignEntry.mutateAsync(params);
            if (response.status) {
                // if (onPost) onPost();
                return response;
            } else {
                setError('Failed to update files !')
            }
            return response;
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
        return true;
    }

    return {
        loading, error,
        handleDownload, handleGetThumbnail, handleMakeEntry, handleDelete, handleUpload, handleFetchFiles
    }

}